'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsInIcon, ArrowsOutIcon, ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import { MediaShell } from '@tranquility-ai/theme-components/media-shell';
import { Modal } from '@tranquility-ai/theme-components/modal';
import {
  EmailArchiveSectionControl,
  type EmailArchiveSection,
} from '@tranquility-ai/theme-components/email-archive-viewer';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { MediaShellHeaderButton } from '@/components/ui/MediaShellHeaderButton';
import { s3KeyBasename } from './utils';
import { ThemeEmailArchiveBody } from './ThemeEmailArchiveViewer';

/**
 * Theme mailbox chrome shared by the in-app preview and the standalone tab.
 *
 * Expanded and page layouts use ``MediaShell`` ``pane`` rather than
 * ``presentation``: presentation drops the header, which would hide the
 * section tabs hosted with ``sectionControl="external"``.
 *
 * The in-app dialog uses ``Modal`` with the built-in close hidden. Expand,
 * open-in-new-tab, and close are the same header buttons, each with a tooltip.
 * ``MediaShell`` modal always renders its own close control, which has no
 * tooltip slot.
 */
export function ThemeEmailArchiveShell({
  s3Key,
  initialEmailIndex = null,
  syncUrl = false,
  variant,
  isOpen = true,
  onClose,
}: {
  s3Key: string;
  initialEmailIndex?: number | null;
  syncUrl?: boolean;
  variant: 'modal' | 'page';
  isOpen?: boolean;
  onClose?: () => void;
}) {
  const t = useTimePilotT();
  const [section, setSection] = useState<EmailArchiveSection>('overview');
  const [expanded, setExpanded] = useState(false);
  const title = s3KeyBasename(s3Key) || t('viewers.loadingMailbox');
  const showFullViewport = variant === 'page' || expanded;

  useEffect(() => {
    if (!showFullViewport || variant !== 'modal') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, showFullViewport, variant]);

  const openInNewTab = () => {
    const params = new URLSearchParams({ s3Key });
    if (typeof window !== 'undefined') {
      const caseId = new URLSearchParams(window.location.search).get('case');
      if (caseId) params.set('caseId', caseId);
    }
    window.open(`/email-viewer?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const sectionControl = (
    <EmailArchiveSectionControl
      section={section}
      onSectionChange={setSection}
      variant="dark"
    />
  );

  const closeTab = () => {
    window.close();
  };

  const expandControl =
    variant === 'page' ? (
      <MediaShellHeaderButton label={t('viewers.closeMailboxTab')} onClick={closeTab}>
        <XIcon size={16} />
      </MediaShellHeaderButton>
    ) : (
      <>
        <MediaShellHeaderButton
          label={expanded ? t('viewers.collapseMailbox') : t('viewers.expandMailbox')}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ArrowsInIcon size={16} /> : <ArrowsOutIcon size={16} />}
        </MediaShellHeaderButton>
        <MediaShellHeaderButton label={t('viewers.openMailboxTab')} onClick={openInNewTab}>
          <ArrowSquareOutIcon size={16} />
        </MediaShellHeaderButton>
        <MediaShellHeaderButton label={t('viewers.closeMailbox')} onClick={() => onClose?.()}>
          <XIcon size={16} />
        </MediaShellHeaderButton>
      </>
    );

  const body = (
    <ThemeEmailArchiveBody
      s3Key={s3Key}
      initialEmailIndex={initialEmailIndex}
      syncUrl={syncUrl}
      section={section}
      onSectionChange={setSection}
      sectionControl="external"
    />
  );

  if (showFullViewport) {
    const frame = (
      <div
        className={
          variant === 'page'
            ? 'flex h-screen w-full flex-col'
            : 'fixed inset-0 z-[100000] flex bg-white'
        }
      >
        <MediaShell
          mode="pane"
          title={title}
          headerActions={sectionControl}
          controls={expandControl}
          className="h-full min-h-0 w-full"
          bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
        >
          <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">{body}</div>
        </MediaShell>
      </div>
    );
    if (variant === 'modal' && typeof document !== 'undefined') {
      return createPortal(frame, document.body);
    }
    return frame;
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
      onClose={onClose}
      showCloseButton={false}
      title={title}
      headerActions={sectionControl}
      controls={expandControl}
      size="xl"
      contentClassName="tp-media-shell !h-[80vh] !min-h-[80vh] !max-h-[80vh]"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">{body}</div>
    </Modal>
  );
}
